import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import { useConfig } from './ConfigContext'

const FilterContext = createContext()

const createInitialState = (defaultCategory = 'health') => ({
  activeCategory: defaultCategory,
  activeFilters: {}, // Structure: { groupId: { filterId: value, ... }, ... }
  expandedGroups: ['services'], // Start with services expanded
  filterCount: 0,
  searchTerm: '', // Search term for filtering by name
})

function filterReducer(state, action) {
  switch (action.type) {
    case 'SET_CATEGORY':
      return {
        ...state,
        activeCategory: action.category,
        activeFilters: {}, // Reset filters when category changes
        filterCount: 0,
        searchTerm: '', // Reset search when category changes
      }

    case 'SET_FILTER': {
      const newFilters = { ...state.activeFilters }
      const { groupId, filterId, value } = action

      // Initialize group if it doesn't exist
      if (!newFilters[groupId]) {
        newFilters[groupId] = {}
      }

      if (value === null || value === '' || value === false) {
        delete newFilters[groupId][filterId]
        // Remove group if empty
        if (Object.keys(newFilters[groupId]).length === 0) {
          delete newFilters[groupId]
        }
      } else {
        newFilters[groupId][filterId] = value
      }

      // Calculate total filter count
      const totalFilters = Object.values(newFilters).reduce((count, group) => {
        return count + Object.keys(group).length
      }, 0)

      return {
        ...state,
        activeFilters: newFilters,
        filterCount: totalFilters,
      }
    }

    case 'CLEAR_FILTERS':
      return {
        ...state,
        activeFilters: {},
        filterCount: 0,
        searchTerm: '', // Clear search term when clearing filters
      }

    case 'SET_SEARCH_TERM':
      return {
        ...state,
        searchTerm: action.searchTerm,
      }

    case 'TOGGLE_GROUP': {
      const expandedGroups = state.expandedGroups.includes(action.groupId)
        ? state.expandedGroups.filter(id => id !== action.groupId)
        : [...state.expandedGroups, action.groupId]

      return {
        ...state,
        expandedGroups,
      }
    }

    default:
      return state
  }
}

export function FilterProvider({ children }) {
  const { getDefaultCategory } = useConfig()
  const [state, dispatch] = useReducer(
    filterReducer,
    createInitialState(getDefaultCategory())
  )
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize from URL params on mount
  useEffect(() => {
    const category = searchParams.get('category')
    const filtersParam = searchParams.get('filters')
    const searchParam = searchParams.get('search')

    if (category && category !== state.activeCategory) {
      dispatch({ type: 'SET_CATEGORY', category })
    }

    if (filtersParam) {
      try {
        const filters = JSON.parse(decodeURIComponent(filtersParam))
        if (typeof filters === 'object' && filters !== null) {
          Object.entries(filters).forEach(([groupId, groupFilters]) => {
            Object.entries(groupFilters).forEach(([filterId, value]) => {
              dispatch({ type: 'SET_FILTER', groupId, filterId, value })
            })
          })
        }
      } catch (error) {
        window.console.warn('Failed to parse filters from URL:', error)
      }
    }

    if (searchParam) {
      dispatch({ type: 'SET_SEARCH_TERM', searchTerm: searchParam })
    }
  }, []) // Only on mount

  const updateURLParams = useCallback(
    newParams => {
      const current = Object.fromEntries(searchParams.entries())
      const updatedParams = { ...current, ...newParams }

      // Remove undefined/null values
      Object.keys(updatedParams).forEach(key => {
        if (updatedParams[key] === null || updatedParams[key] === undefined) {
          delete updatedParams[key]
        }
      })

      setSearchParams(updatedParams)
    },
    [searchParams, setSearchParams]
  )

  const setCategory = useCallback(
    category => {
      dispatch({ type: 'SET_CATEGORY', category })
      updateURLParams({ category, filters: undefined, search: undefined }) // Clear filters and search when category changes
    },
    [updateURLParams]
  )

  const setFilter = useCallback(
    (groupId, filterId, value) => {
      dispatch({ type: 'SET_FILTER', groupId, filterId, value })

      // Update URL with current filters after the action
      setTimeout(() => {
        // Calculate what the new filters will be after the dispatch
        const newFilters = { ...state.activeFilters }

        // Initialize group if it doesn't exist
        if (!newFilters[groupId]) {
          newFilters[groupId] = {}
        }

        if (value === null || value === '' || value === false) {
          delete newFilters[groupId][filterId]
          // Remove group if empty
          if (Object.keys(newFilters[groupId]).length === 0) {
            delete newFilters[groupId]
          }
        } else {
          newFilters[groupId][filterId] = value
        }

        // Check if we have any filters left
        const hasFilters =
          Object.keys(newFilters).length > 0 &&
          Object.values(newFilters).some(group => Object.keys(group).length > 0)

        const filtersString = hasFilters
          ? encodeURIComponent(JSON.stringify(newFilters))
          : undefined

        updateURLParams({ filters: filtersString })
      }, 0)
    },
    [updateURLParams, state.activeFilters]
  )

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' })
    updateURLParams({ filters: undefined, search: undefined })
  }, [updateURLParams])

  const setSearchTerm = useCallback(
    searchTerm => {
      dispatch({ type: 'SET_SEARCH_TERM', searchTerm })
      updateURLParams({ search: searchTerm || undefined })
    },
    [updateURLParams]
  )

  const toggleGroup = useCallback(groupId => {
    dispatch({ type: 'TOGGLE_GROUP', groupId })
  }, [])

  const isGroupExpanded = useCallback(
    groupId => {
      return state.expandedGroups.includes(groupId)
    },
    [state.expandedGroups]
  )

  const getFilterValue = useCallback(
    (groupId, filterId) => {
      return state.activeFilters[groupId]?.[filterId] || null
    },
    [state.activeFilters]
  )

  const getGroupFilters = useCallback(
    groupId => {
      return state.activeFilters[groupId] || {}
    },
    [state.activeFilters]
  )

  const hasActiveFilters = useCallback(() => {
    return state.filterCount > 0 || state.searchTerm.length > 0
  }, [state.filterCount, state.searchTerm])

  const value = {
    ...state,
    setCategory,
    setFilter,
    clearFilters,
    setSearchTerm,
    toggleGroup,
    isGroupExpanded,
    getFilterValue,
    getGroupFilters,
    hasActiveFilters,
  }

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  )
}

export function useFilters() {
  const context = useContext(FilterContext)
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider')
  }
  return context
}

FilterProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
